import { format as dateFnsFormat, formatDistanceToNow, parse } from 'date-fns';

import { DEFAULT_DATE_FORMAT } from '#constants/date';
import { isNotDefined } from '#misc/is-not-defined.guard';

import type { DateFormatOptions } from './types/date-format.options';

/**
 * Formats a given date according to the specified format or options.
 *
 * - If a string date is provided, a `parseFormat` must be specified in the options to parse the string.
 * - Supports both absolute and relative date formatting.
 * - If `isRelative` is true in the options, returns a human-readable relative time (e.g., "3 days ago").
 * - Otherwise, formats the date using the provided or default format string.
 *
 * @param date - The date to format, either as a `Date` object or a string.
 * @param format - The format to use, either as a format string or a `DateFormatOptions` object.
 * @returns The formatted date string.
 * @throws If `date` is a string and `parseFormat` is not provided in the options.
 */
export const formatDate = (date: Date | string, format: DateFormatOptions | string = DEFAULT_DATE_FORMAT): string => {
  const isOptions = typeof format !== 'string';
  let parsed: Date;
  let formatString: string | undefined;
  let isRelative = false;
  let parseFormat: string | undefined;

  if (isOptions) {
    ({ format: formatString = DEFAULT_DATE_FORMAT, isRelative = false, parseFormat } = format);
  }

  if (typeof date === 'string') {
    if (!isOptions || isNotDefined(parseFormat)) {
      throw new Error('parseFormat is required when date is of type string');
    }

    parsed = parse(date, parseFormat, new Date());
  } else {
    parsed = date;
  }

  return isRelative
    ? formatDistanceToNow(parsed, { addSuffix: true, includeSeconds: true })
    : dateFnsFormat(parsed, isOptions ? (formatString ?? DEFAULT_DATE_FORMAT) : format);
};
